import {
  ComponentType,
  createElement,
  CSSProperties,
  isValidElement,
  ReactNode
} from 'react';
import clsx from 'clsx';
import { Table } from './components/Table';
import { TableBody } from './components/TableBody';
import { TableCell } from './components/TableCell';
import { TableHeader } from './components/TableHeader';
import { TableRow } from './components/TableRow';

import styles from './index.module.css';

export interface Column<T> {
  Header: ReactNode | ComponentType<object>;
  accessor: keyof T;
  DataComponent?: ComponentType<{ row: T; index: number }>;
  cellClass?: string;
  id?: string;
}

interface ClassNames {
  table?: string;
  tHeadRow?: string;
  tHeadCell?: string;
  customRow?: string;
  primaryRow?: string;
  secondaryRow?: string;
  childRow?: string;
  customCell?: string;
}

interface DataTableProps<T> {
  data: Array<T & { isChild?: boolean; id: string | number }>;
  columns: Column<T>[];
  withHeader?: boolean;
  className?: string;
  classNames?: ClassNames;
  customCellClass?: CSSProperties;
  tableRowStyle?: CSSProperties;
  isClearAfter?: boolean;
  size?: 's' | 'm' | 'l';
}

export const DataTable = <T,>({
  data,
  columns,
  withHeader = true,
  className = '',
  classNames = {},
  customCellClass,
  tableRowStyle,
  isClearAfter,
  size = 's'
}: DataTableProps<T>) => {
  let extraRowClassName: string | undefined;
  const {
    table,
    tHeadRow,
    tHeadCell,
    customRow,
    primaryRow,
    secondaryRow,
    childRow,
    customCell
  } = classNames;
  const tableClassName = clsx(table || className);

  return (
    <Table className={tableClassName}>
      {withHeader && (
        <TableHeader>
          <TableRow className={clsx([tHeadRow || styles.row, styles[size]])}>
            {columns.map(({ Header, id }) => {
              let headerContent: ReactNode;

              if (isValidElement(Header)) {
                headerContent = Header;
              } else if (typeof Header === 'function') {
                headerContent = createElement(Header);
              } else {
                headerContent = Header;
              }

              return (
                <TableCell
                  scope="col"
                  key={id || String(Header)}
                  className={clsx([
                    tHeadCell,
                    styles.head,
                    isClearAfter && styles.clearAfter
                  ])}
                  style={customCellClass}
                >
                  {headerContent}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHeader>
      )}
      <TableBody>
        {data.map(({ isChild: isChildRow, ...row }, index) => {
          if (!isChildRow && !extraRowClassName) {
            extraRowClassName = primaryRow;
          } else if (!isChildRow) {
            extraRowClassName =
              extraRowClassName === primaryRow ? secondaryRow : primaryRow;
          }
          const rowClass = clsx(
            isChildRow && childRow,
            extraRowClassName,
            customRow || styles.row,
            styles.contentRow
          );
          const rowKey = `row-table-${index}`;
          return (
            <TableRow
              key={rowKey}
              className={clsx([rowClass, styles[size]])}
              style={tableRowStyle}
            >
              {columns.map(
                ({ accessor, DataComponent, cellClass }, indexColumn) => {
                  const key = `row-${index}-column-${String(accessor)}`;
                  return (
                    <TableCell
                      key={key}
                      className={clsx([
                        customCell,
                        cellClass,
                        styles.cell,
                        isClearAfter && styles.clearAfter
                      ])}
                      style={customCellClass}
                    >
                      {DataComponent ? (
                        <DataComponent row={row as T} index={indexColumn} />
                      ) : (
                        ((row as T)[accessor] as ReactNode)
                      )}
                    </TableCell>
                  );
                }
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
